import { useEffect, useRef, type ReactNode } from 'react'
import { useLocation } from 'react-router'

/**
 * Wraps every page. On each pathname change moves focus to #main-content for
 * keyboard/screen-reader users. Scroll position is handled by React Router's
 * <ScrollRestoration> in root.tsx (top on navigation, restored on back/forward).
 * Skips the very first render and respects hash links.
 */
export function RootLayout({ children }: { children: ReactNode }) {
  const { pathname, hash } = useLocation()
  const firstRender = useRef(true)

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    if (hash) return // let the browser handle in-page anchor scrolling

    // Move focus to main content without scrolling it back into view.
    const main = document.getElementById('main-content')
    if (main) {
      const hadTabIndex = main.hasAttribute('tabindex')
      if (!hadTabIndex) main.setAttribute('tabindex', '-1')
      main.focus({ preventScroll: true })
      if (!hadTabIndex) {
        // Drop the temporary tabindex once focus leaves, so it isn't tab-stoppable.
        main.addEventListener('blur', () => main.removeAttribute('tabindex'), { once: true })
      }
    }
  }, [pathname, hash])

  return <>{children}</>
}
